import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import {
  WIZARD_ACTIVATING_INDICATOR_DELAY_MILLISECONDS,
  WIZARD_ACTIVATING_TEXT_DELAY_MILLISECONDS,
  WIZARD_BODY_CLASS_NAME,
  WIZARD_SCREEN_SUBTITLE_CLASS_NAME,
  WIZARD_SCREEN_TITLE_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import type { tWizardActivatingStage } from './WhatsAppConnectionWizard.types';

export type tWhatsAppConnectionWizardActivatingProps = {
  /** Where the screen starts; a real exchange starts from the first moment. */
  startStage: tWizardActivatingStage;
};

/** The steps after Meta answers, done, running and still to come. */
const ACTIVATING_STEPS = [
  {
    textKey: 'wizard.activating.steps.metaResponded',
    icon: CheckCircleIcon,
    iconClassName: 'text-[var(--ls-text-success)]',
  },
  {
    textKey: 'wizard.activating.steps.activateWithMeta',
    icon: ArrowPathIcon,
    iconClassName: 'text-[var(--ls-text-primary-default)]',
  },
  {
    textKey: 'wizard.activating.steps.receiveMessages',
    icon: ChatBubbleLeftRightIcon,
    iconClassName: 'text-[var(--ls-text-neutral-light)]',
  },
];

/**
 * The main pane while Leadsales exchanges Meta's code and activates the
 * number. Following the DS loading rule, the indicator shows from about
 * 400 ms and the text and steps from about 3 s, so a quick answer never
 * flashes them. The status region is mounted from the first moment, so the
 * text it gets is announced.
 *
 * @param {tWhatsAppConnectionWizardActivatingProps} props - Where it starts.
 * @returns {JSX.Element} The activating screen.
 */
export default function WhatsAppConnectionWizardActivating(
  props: tWhatsAppConnectionWizardActivatingProps,
) {
  const { startStage } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const startsExplained = startStage !== 'start';
  const [showsIndicator, setShowsIndicator] = useState(startsExplained);
  const [showsText, setShowsText] = useState(startsExplained);

  useEffect(() => {
    if (startsExplained) return undefined;
    const indicatorTimer = setTimeout(
      () => setShowsIndicator(true),
      WIZARD_ACTIVATING_INDICATOR_DELAY_MILLISECONDS,
    );
    const textTimer = setTimeout(
      () => setShowsText(true),
      WIZARD_ACTIVATING_TEXT_DELAY_MILLISECONDS,
    );
    return () => {
      clearTimeout(indicatorTimer);
      clearTimeout(textTimer);
    };
  }, [startsExplained]);

  let lastLine: string | null = null;
  if (showsText) {
    lastLine = t(
      startStage === 'overThirtySeconds'
        ? 'wizard.activating.slowLine'
        : 'wizard.activating.lastLine',
    );
  }

  return (
    <div className="flex flex-col gap-[var(--ls-spacing-6)]">
      <div className="flex flex-col gap-[var(--ls-spacing-2)]">
        <h2 className={WIZARD_SCREEN_TITLE_CLASS_NAME}>
          {t('wizard.activating.title')}
        </h2>
        <p className={WIZARD_SCREEN_SUBTITLE_CLASS_NAME}>
          {t('wizard.activating.subtitle')}
        </p>
      </div>
      <div
        role="status"
        className="flex min-h-6 items-center gap-[var(--ls-spacing-3)]"
      >
        {/* The DS circular indicator exists only as CSS this app does not
            load, so it is built here with its loop token. */}
        {showsIndicator && (
          <span
            data-testid="wizard-activating-indicator"
            aria-hidden="true"
            className="h-6 w-6 shrink-0 animate-[spin_var(--ls-motion-progress-circular-rotate)_infinite] rounded-[var(--ls-radius-full)] border-[3px] border-[var(--ls-border-neutral-lighter)] border-t-[var(--ls-accent-primary-default)]"
          />
        )}
        {showsText && (
          <p className={classNames(WIZARD_BODY_CLASS_NAME, 'font-medium')}>
            {t('wizard.activating.loading')}
          </p>
        )}
      </div>
      {showsText && (
        <ol className="flex flex-col gap-[var(--ls-spacing-3)]">
          {ACTIVATING_STEPS.map((step) => (
            <li
              key={step.textKey}
              className={classNames(
                WIZARD_BODY_CLASS_NAME,
                'flex gap-[var(--ls-spacing-2)]',
              )}
            >
              <step.icon
                className={classNames(
                  'mt-0.5 h-5 w-5 shrink-0',
                  step.iconClassName,
                )}
                aria-hidden="true"
              />
              {t(step.textKey)}
            </li>
          ))}
        </ol>
      )}
      {/* Its own live region, so the longer-than-usual line is announced
          when it replaces the first one. */}
      <p
        aria-live="polite"
        className="text-[length:var(--ls-body)] text-[var(--ls-text-neutral-light)]"
      >
        {lastLine}
      </p>
    </div>
  );
}
